/**
 * Wizard mercato — crea proposta o controofferta (FASE 3 iter 6).
 *
 * Step:
 *   1. Tipo operazione (1 dei 8 tipi)
 *   2. Squadra destinataria + finestra
 *   3. Giocatori (1+ con direzione entra/esce, condizionata al tipo)
 *   4. Importi/condizioni + messaggio
 *
 * CONTROFFERTA (Andrea 2026-09-10): passando `controproposta` il wizard
 * riparte dall'offerta ricevuta e crea la figlia via
 * POST /mercato/operazioni/{id}/contropponi. La controparte e' fissa
 * (chi ha proposto) quindi lo step 2 salta, e i campi arrivano
 * "specchiati" dal punto di vista di chi contropropone:
 *   - le direzioni dei giocatori si girano (entra <-> esce)
 *   - acquisto <-> vendita (chi riceve l'offerta risponde vendendo)
 *   - il conguaglio cambia segno (positivo = paga il proponente)
 * Cosi' l'affare resta lo stesso e chi risponde ritocca solo la cifra.
 *
 * Andrea 2026-09-10: la controfferta si puo' pero' riscrivere da zero —
 * tipo, giocatori, direzioni e importi sono tutti modificabili nello
 * step 3 (nella proposta nuova invece il tipo resta la scelta guidata
 * dello step 1, con le direzioni fissate dal tipo).
 *
 * Bridge v1.4 Sez. 1.12 + App. D.
 */

import { useMemo, useState } from "react";
import {
  ContropponiPayload,
  CreaPropostaPayload,
  DirezioneGiocatore,
  GiocatoreOpItem,
  OperazioneItem,
  TIPI_OPERAZIONE,
  TipoOperazione,
  metaPerTipo,
  useContropponi,
  useCreaProposta,
} from "../../hooks/useFase3";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { SquadraSelector } from "../common/SquadraSelector";
import { GiocatoreSquadraSelector } from "../common/GiocatoreSquadraSelector";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";

interface Props {
  /** Squadra del presidente loggato (proponente). UUID. */
  squadraProponenteId: string;
  /** Finestra mercato attiva. Se vuota, l'utente la inserisce manualmente. */
  finestraIdDefault?: string;
  /** Offerta ricevuta a cui si sta contropponendo (modalita' controfferta). */
  controproposta?: OperazioneItem;
  onClose: () => void;
  onCreated?: (opId: string) => void;
}

const OPPOSTA: Record<DirezioneGiocatore, DirezioneGiocatore> = {
  entra: "esce",
  esce: "entra",
};

/** Il tipo con cui si risponde: chi riceve un acquisto risponde con una
 * vendita (e viceversa), cosi' i soldi continuano a muoversi nel verso
 * giusto. Gli altri tipi restano quelli dell'offerta. */
const TIPO_SPECULARE: Partial<Record<TipoOperazione, TipoOperazione>> = {
  acquisto: "vendita",
  vendita: "acquisto",
};

/** Cambia segno a un importo (il conguaglio e' sempre dal POV del
 * proponente: se prima lo pagava lui, ora lo incassa). */
function segnoInvertito(importo: string | null | undefined): string {
  if (!importo) return "";
  const n = Number(importo);
  if (!Number.isFinite(n) || n === 0) return "";
  return String(-n);
}

type Step = 1 | 2 | 3 | 4;

interface GiocatoreLocale {
  uuid: string;
  giocatore_id: string;
  direzione: DirezioneGiocatore;
}

function nuovoGiocatoreLocale(direzione: DirezioneGiocatore): GiocatoreLocale {
  return {
    uuid: crypto.randomUUID(),
    giocatore_id: "",
    direzione,
  };
}

export function MercatoWizard({
  squadraProponenteId,
  finestraIdDefault,
  controproposta,
  onClose,
  onCreated,
}: Props) {
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneIdActive = stagioneAttiva?.id ?? null;
  const padre = controproposta ?? null;
  // In controfferta tipo e controparte sono dati: si parte dai giocatori.
  const [step, setStep] = useState<Step>(padre ? 3 : 1);
  const [tipo, setTipo] = useState<TipoOperazione | null>(
    padre ? (TIPO_SPECULARE[padre.tipo as TipoOperazione] ?? (padre.tipo as TipoOperazione)) : null,
  );
  // Finestra auto: viene passata da MercatoPage (finestra attiva della stagione)
  const finestraId = finestraIdDefault ?? "";
  const [squadraDestinatariaId, setSquadraDestinatariaId] = useState(
    padre?.squadra_proponente_id ?? "",
  );

  const [giocatori, setGiocatori] = useState<GiocatoreLocale[]>(() =>
    padre
      ? padre.giocatori.map((g) => ({
          uuid: crypto.randomUUID(),
          giocatore_id: g.giocatore_id,
          direzione: OPPOSTA[g.direzione],
        }))
      : [],
  );
  const [importoOfferto, setImportoOfferto] = useState(padre?.importo_offerto_milioni ?? "");
  const [conguaglio, setConguaglio] = useState(
    padre ? segnoInvertito(padre.conguaglio_milioni) : "",
  );
  const [importoRiscatto, setImportoRiscatto] = useState(padre?.importo_riscatto_milioni ?? "");
  const [condizioneRiscatto, setCondizioneRiscatto] = useState(
    padre?.condizione_riscatto_testo ?? "",
  );
  const [messaggio, setMessaggio] = useState("");

  const meta = useMemo(() => (tipo ? metaPerTipo(tipo) : null), [tipo]);
  const creaMut = useCreaProposta();
  const contropponiMut = useContropponi();
  const mut = padre ? contropponiMut : creaMut;

  // === Quando l'utente sceglie il tipo, inizializza i giocatori ===
  function scegliTipo(t: TipoOperazione) {
    setTipo(t);
    const m = metaPerTipo(t);
    if (padre) {
      // Controfferta: il tipo si cambia in corsa, senza perdere
      // giocatori e cifre gia' impostati.
      setStep(3);
      return;
    }
    // Reset campi importo
    setImportoOfferto("");
    setConguaglio("");
    setImportoRiscatto("");
    setCondizioneRiscatto("");
    // Almeno 1 giocatore nella direzione principale; per scambi: +1 nella direzione opposta
    const iniziali: GiocatoreLocale[] = [nuovoGiocatoreLocale(m.direzionePrincipale)];
    if (m.ammetteScambio) {
      iniziali.push(nuovoGiocatoreLocale(OPPOSTA[m.direzionePrincipale]));
    }
    setGiocatori(iniziali);
    setStep(2);
  }

  function addGiocatore(direzione: DirezioneGiocatore) {
    setGiocatori((g) => [...g, nuovoGiocatoreLocale(direzione)]);
  }
  function removeGiocatore(uuid: string) {
    setGiocatori((g) => g.filter((x) => x.uuid !== uuid));
  }
  function setGiocatoreId(uuid: string, val: string) {
    setGiocatori((g) => g.map((x) => (x.uuid === uuid ? { ...x, giocatore_id: val } : x)));
  }
  /** Gira il lato di una riga: il giocatore scelto era dell'altra rosa,
   * quindi si azzera e va riscelto. */
  function setDirezione(uuid: string, direzione: DirezioneGiocatore) {
    setGiocatori((g) =>
      g.map((x) => (x.uuid === uuid ? { ...x, direzione, giocatore_id: "" } : x)),
    );
  }
  /** Cambia tipo SENZA buttare via il lavoro fatto (solo controfferta):
   * cambiano i campi importo richiesti, giocatori e cifre restano. */
  function cambiaTipo(t: TipoOperazione) {
    setTipo(t);
  }

  // === Validazioni step-by-step ===
  function step2Valido() {
    // Finestra mercato e' implicita: viene passata da finestraIdDefault
    // (la pagina /mercato la setta sulla finestra attiva).
    return (
      finestraId.trim().length > 0 &&
      squadraDestinatariaId.trim().length > 0 &&
      squadraDestinatariaId.trim() !== squadraProponenteId
    );
  }

  function step3Valido() {
    if (!meta) return false;
    if (giocatori.length === 0) return false;
    if (giocatori.some((g) => !g.giocatore_id.trim())) return false;
    // Per scambi: serve almeno 1 in ciascuna direzione
    if (meta.ammetteScambio) {
      const haEntra = giocatori.some((g) => g.direzione === "entra");
      const haEsce = giocatori.some((g) => g.direzione === "esce");
      if (!haEntra || !haEsce) return false;
    }
    return true;
  }

  function step4Valido(): boolean {
    if (!meta) return false;
    if (meta.campi.importoOfferto === "required" && !importoOfferto.trim()) return false;
    if (meta.campi.conguaglio === "required" && !conguaglio.trim()) return false;
    if (meta.campi.importoRiscatto === "required" && !importoRiscatto.trim()) return false;
    if (meta.campi.condizioneRiscatto === "required" && !condizioneRiscatto.trim()) return false;
    return true;
  }

  async function submit() {
    if (!tipo || !meta || !step4Valido()) return;
    const importi = {
      importo_offerto_milioni:
        meta.campi.importoOfferto !== "hidden" && importoOfferto.trim()
          ? importoOfferto.trim()
          : null,
      conguaglio_milioni:
        meta.campi.conguaglio !== "hidden" && conguaglio.trim() ? conguaglio.trim() : null,
      importo_riscatto_milioni:
        meta.campi.importoRiscatto !== "hidden" && importoRiscatto.trim()
          ? importoRiscatto.trim()
          : null,
      condizione_riscatto_testo:
        meta.campi.condizioneRiscatto !== "hidden" && condizioneRiscatto.trim()
          ? condizioneRiscatto.trim()
          : null,
      messaggio: messaggio.trim() || null,
    };
    const giocatoriPayload = giocatori.map<GiocatoreOpItem>((g) => ({
      giocatore_id: g.giocatore_id.trim(),
      direzione: g.direzione,
    }));
    if (padre) {
      // Controfferta: finestra e squadre le ricava il backend dal padre.
      const payload: ContropponiPayload = {
        tipo,
        giocatori: giocatoriPayload,
        ...importi,
      };
      try {
        const op = await contropponiMut.mutateAsync({
          opId: padre.id,
          payload,
        });
        onCreated?.(op.id);
        onClose();
      } catch {
        // Errore mostrato sotto il bottone via contropponiMut.error
      }
      return;
    }
    const payload: CreaPropostaPayload = {
      finestra_id: finestraId.trim(),
      tipo,
      squadra_proponente_id: squadraProponenteId,
      squadra_destinataria_id: squadraDestinatariaId.trim(),
      giocatori: giocatoriPayload,
      ...importi,
    };
    try {
      const op = await creaMut.mutateAsync(payload);
      onCreated?.(op.id);
      onClose();
    } catch {
      // Errore mostrato sotto il bottone via creaMut.error
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg border border-accent/30 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-primary-deep px-6 py-4 border-b border-accent/30 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h3 className="font-display text-xl text-accent tracking-wide">
              {padre ? tr("Controfferta") : tr("Nuova operazione di mercato")}
            </h3>
            <p className="text-xs text-fg-muted mt-1">
              {padre
                ? tr("a {{v1}} · ", { v1: padre.nome_squadra_proponente ?? "controparte" })
                : tr("Step {{v1}}/4 · ", { v1: step })}
              {tipo
                ? tr("{{v1}} {{v2}}", { v1: metaPerTipo(tipo).emoji, v2: metaPerTipo(tipo).label })
                : tr("Scegli tipo")}
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* ============= STEP 1: SCEGLI TIPO ============= */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TIPI_OPERAZIONE.map((m) => (
                <button
                  key={etichetta(m.tipo)}
                  onClick={() => scegliTipo(m.tipo)}
                  className="card text-left hover:border-accent transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{m.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-accent group-hover:text-accent">{m.label}</div>
                      <div className="text-xs text-fg-muted mt-1">{m.descrizione}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ============= STEP 2: DESTINATARIA ============= */}
          {step === 2 && meta && (
            <>
              <div>
                <SquadraSelector
                  value={squadraDestinatariaId}
                  onChange={setSquadraDestinatariaId}
                  label="Squadra destinataria"
                  autoSelectMine={false}
                />
                {squadraDestinatariaId.trim() === squadraProponenteId && (
                  <p className="text-xs text-danger mt-1">
                    {tr("La squadra destinataria non può coincidere con la tua.")}
                  </p>
                )}
              </div>
              {/* Andrea 2026-05-20: rimosso campo scadenza per-operazione.
                  Tutte le pendenti vengono auto-annullate alla chiusura
                  della finestra di mercato. */}
              <div className="text-[10px] text-fg-muted italic">
                {tr(
                  tr(
                    "Le proposte pendenti verranno annullate automaticamente alla chiusura della finestra di mercato.",
                  ),
                )}
              </div>
            </>
          )}

          {/* Promemoria dell'offerta a cui si sta rispondendo. */}
          {padre && step > 1 && (
            <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs space-y-1">
              <div className="font-bold text-info uppercase tracking-wider text-[10px]">
                Offerta ricevuta da {padre.nome_squadra_proponente ?? "controparte"}
              </div>
              <div className="text-fg">
                {metaPerTipo(padre.tipo as TipoOperazione).emoji}{" "}
                {metaPerTipo(padre.tipo as TipoOperazione).label}
                {padre.giocatori.length > 0 && (
                  <>
                    {" · "}
                    {padre.giocatori.map((g) => g.nome ?? g.giocatore_id.slice(0, 8)).join(", ")}
                  </>
                )}
                {padre.importo_offerto_milioni && (
                  <> · {Number(padre.importo_offerto_milioni).toFixed(2)}M</>
                )}
                {padre.conguaglio_milioni && (
                  <> · conguaglio {Number(padre.conguaglio_milioni).toFixed(2)}M</>
                )}
              </div>
              {padre.messaggio_proponente && (
                <div className="text-fg-muted italic">“{padre.messaggio_proponente}”</div>
              )}
              <div className="text-fg-muted">
                {tr(
                  tr(
                    "I campi partono da questa offerta girata dal tuo lato: ritocca cifre e giocatori e rimandala indietro.",
                  ),
                )}
              </div>
            </div>
          )}

          {/* ============= STEP 3: GIOCATORI ============= */}
          {step === 3 && meta && (
            <>
              {/* In controfferta il tipo si cambia qui, senza tornare
                  indietro e senza perdere quello che hai impostato. */}
              {padre && (
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    {tr("Tipo operazione")}
                  </label>
                  <select
                    value={tipo ?? ""}
                    onChange={(e) => cambiaTipo(e.target.value as TipoOperazione)}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {TIPI_OPERAZIONE.map((m) => (
                      <option key={m.tipo} value={m.tipo}>
                        {m.emoji} {m.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-fg-muted mt-1">
                    {tr(
                      tr(
                        "Cambiando tipo restano giocatori e cifre: cambiano solo i campi importo richiesti dallo step successivo.",
                      ),
                    )}
                  </p>
                </div>
              )}
              <div className="bg-primary-deep/40 rounded-lg p-3 text-xs text-fg-muted">
                <strong className="text-accent">{tr("Direzione:")}</strong>{" "}
                <span className="text-success">{tr("entra")}</span> = il giocatore arriva a te ·{" "}
                <span className="text-warning">{tr("esce")}</span> = il giocatore lascia la tua
                rosa.
              </div>
              <div className="space-y-2">
                {giocatori.map((g) => {
                  // 'entra' = giocatore della destinataria che arriva a me
                  // 'esce' = giocatore della mia squadra che va alla destinataria
                  const fromSquadra =
                    g.direzione === "entra" ? squadraDestinatariaId : squadraProponenteId;
                  const altriGiaSel = giocatori
                    .filter((x) => x.uuid !== g.uuid)
                    .map((x) => x.giocatore_id)
                    .filter(Boolean);
                  return (
                    <div key={g.uuid} className="flex items-center gap-2">
                      {padre ? (
                        // Controfferta: il lato di ogni giocatore e'
                        // modificabile (si puo' rigirare l'affare).
                        <select
                          value={g.direzione}
                          onChange={(e) =>
                            setDirezione(g.uuid, e.target.value as DirezioneGiocatore)
                          }
                          title={tr("Da che parte va questo giocatore")}
                          className={`shrink-0 px-2 py-1.5 rounded-lg border text-xs font-bold focus:outline-none focus:ring-2 focus:ring-accent ${
                            g.direzione === "entra"
                              ? "bg-success/20 text-success border-success/40"
                              : "bg-warning/20 text-warning border-warning/40"
                          }`}
                        >
                          <option value="entra">{tr("entra")}</option>
                          <option value="esce">{tr("esce")}</option>
                        </select>
                      ) : (
                        <span
                          className={`pill ${
                            g.direzione === "entra"
                              ? "bg-success/20 text-success"
                              : "bg-warning/20 text-warning"
                          }`}
                        >
                          {g.direzione}
                        </span>
                      )}
                      <div className="flex-1">
                        <GiocatoreSquadraSelector
                          stagioneId={stagioneIdActive}
                          squadraId={fromSquadra || null}
                          value={g.giocatore_id}
                          onChange={(id) => setGiocatoreId(g.uuid, id)}
                          excludeIds={altriGiaSel}
                          // Cedibilità: per ogni giocatore mostrato in
                          // operazione (sia "entra" che "esce") si parte
                          // dall'altra squadra → mostra solo quelli che
                          // quella squadra può effettivamente cedere
                          // (Andrea 2026-05-19: niente giocatori in
                          // prestito nelle vendite/richieste).
                          soloCedibili
                        />
                      </div>
                      {giocatori.length > 1 && (
                        <button
                          onClick={() => removeGiocatore(g.uuid)}
                          className="btn-ghost text-sm text-danger"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* In controfferta si aggiunge su entrambi i lati a
                    prescindere dal tipo: l'affare si puo' riscrivere. */}
                {meta.ammetteScambio || padre ? (
                  <>
                    <button onClick={() => addGiocatore("entra")} className="btn-ghost text-sm">
                      {tr("+ Aggiungi giocatore in entrata")}
                    </button>
                    <button onClick={() => addGiocatore("esce")} className="btn-ghost text-sm">
                      {tr("+ Aggiungi giocatore in uscita")}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => addGiocatore(meta.direzionePrincipale)}
                    className="btn-ghost text-sm"
                  >
                    {tr("+ Aggiungi giocatore")}
                  </button>
                )}
              </div>
            </>
          )}

          {/* ============= STEP 4: IMPORTI + MESSAGGIO ============= */}
          {step === 4 && meta && (
            <>
              {meta.campi.importoOfferto !== "hidden" && (
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Importo offerto (fantamilioni)
                    {meta.campi.importoOfferto === "required" && (
                      <span className="text-danger ml-1">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={importoOfferto}
                    onChange={(e) => setImportoOfferto(e.target.value)}
                    placeholder={tr("es. 12.50")}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              )}
              {meta.campi.conguaglio !== "hidden" && (
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Conguaglio (fantamilioni, positivo = paghi tu)
                    {meta.campi.conguaglio === "required" && (
                      <span className="text-danger ml-1">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={conguaglio}
                    onChange={(e) => setConguaglio(e.target.value)}
                    placeholder={tr("es. -5.00 (incassi) o 5.00 (paghi)")}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              )}
              {meta.campi.importoRiscatto !== "hidden" && (
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Importo riscatto (fantamilioni)
                    {meta.campi.importoRiscatto === "required" && (
                      <span className="text-danger ml-1">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={importoRiscatto}
                    onChange={(e) => setImportoRiscatto(e.target.value)}
                    placeholder={tr("es. 25.00")}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              )}
              {meta.campi.condizioneRiscatto !== "hidden" && (
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Condizione riscatto (testo)
                    {meta.campi.condizioneRiscatto === "required" && (
                      <span className="text-danger ml-1">*</span>
                    )}
                  </label>
                  <textarea
                    value={condizioneRiscatto}
                    onChange={(e) => setCondizioneRiscatto(e.target.value)}
                    placeholder='es. "Riscatto se gioca 1500+ minuti in stagione"'
                    rows={2}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              )}
              <div>
                <label className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Messaggio per la controparte (opzionale)")}
                </label>
                <textarea
                  value={messaggio}
                  onChange={(e) => setMessaggio(e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              {mut.error && (
                <div className="text-sm text-danger bg-danger/10 rounded-lg p-3">
                  Errore:{" "}
                  {(() => {
                    const err = mut.error as {
                      response?: { data?: { detail?: string } };
                      message?: string;
                    };
                    return err.response?.data?.detail ?? err.message ?? "sconosciuto";
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer con navigation */}
        <div className="bg-primary-deep/40 px-6 py-4 border-t border-primary-soft flex items-center justify-between sticky bottom-0">
          <button
            onClick={() => {
              // In controfferta lo step 3 e' il primo: il tipo si cambia
              // dal menu dentro lo step, non tornando allo step 1.
              if (step === 1 || (padre && step === 3)) onClose();
              else setStep((s) => (s - 1) as Step);
            }}
            className="btn-ghost text-sm"
            disabled={mut.isPending}
          >
            {step === 1 || (padre && step === 3) ? tr("Annulla") : tr("← Indietro")}
          </button>
          <div className="flex items-center gap-2">
            {step < 4 && (
              <button
                onClick={() => setStep((s) => (s + 1) as Step)}
                disabled={
                  (step === 2 && !step2Valido()) || (step === 3 && !step3Valido()) || step === 1
                }
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {tr("Avanti →")}
              </button>
            )}
            {step === 4 && (
              <button
                onClick={submit}
                disabled={!step4Valido() || mut.isPending}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mut.isPending
                  ? tr("Invio…")
                  : padre
                    ? tr("Invia controfferta")
                    : tr("Crea proposta")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
